import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { BrowseCourse } from '@udemy-v2/browse-course';
import { useI18n } from '@udemy-v2/i18n';
import { TrackableDiscoveryItem } from '@udemy-v2/react-browse-event-tracking';
import { withBundleUnit, FrequentlyBoughtTogether } from '@udemy-v2/react-bundle-unit';
import { useRecommendationsSections, RecommendationItem } from '@udemy-v2/react-recommendations';
import { AddToCartButton } from '@udemy-v2/react-shopping-buttons';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { withPriceEventTrackingContext } from '@/features/course-landing-page/events/with-price-event-tracking-context';

/**
 * Why do we compare section IDs?
 *
 * The "Frequently Bought Together" surface can render for two backend-defined section types
 * that come from the GraphQL recommendations response:
 *  - Instructor Led Bundle (ILB) → section id '756138595'
 *  - Standard Frequently Bought Together (FBT) → section id '105216886'
 *
 * Product requirement (EIP-2198): the "extra discount" badge must appear only for ILB sections,
 * not for standard FBT. Until the API exposes an explicit discriminator (e.g., section.type),
 * we gate the badge by checking the section id returned by the backend.
 *
 * Important:
 *  - These ids reflect the current backend contract. If the contract changes, update the ids
 *    here or replace this check with a future explicit section type once available.
 *  - We compare as strings to avoid numeric/BigInt pitfalls when coercing ids.
 *  - Tests document both ids and the gating behavior in:
 *    features/course-landing-page/components/bundle-unit/frequently-bought-together/__tests__/
 *    frequently-bought-together.react-component.spec.tsx
 */
const INSTRUCTOR_BUNDLE_SECTION_ID = '756138595';
//const FBT_BUNDLE_SECTION_ID = '105216886';

export const InternalFrequentlyBoughtTogether = () => {
  const $ = _c(22);
  const {
    data: sections,
    status
  } = useRecommendationsSections();
  const {
    gettext
  } = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["clp_fbt_extra_discount_badge"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  let t1;
  if ($[1] !== featureVariantAssignments) {
    t1 = featureVariantAssignments.get("clp_fbt_extra_discount_badge")?.configuration?.showExtraDiscountBadge;
    $[1] = featureVariantAssignments;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const showExtraDiscountBadgeConfig = t1;
  const showExtraDiscountBadge = typeof showExtraDiscountBadgeConfig === "boolean" ? showExtraDiscountBadgeConfig : false;
  let t2;
  if ($[3] !== sections?.[0]?.id) {
    t2 = sections?.[0]?.id?.toString?.() ?? "";
    $[3] = sections?.[0]?.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sectionId = t2;
  const isInstructorBundle = sectionId === INSTRUCTOR_BUNDLE_SECTION_ID;
  let t3;
  bb0: {
    if (status === "loading" || sections.length < 1) {
      let t4;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      t3 = t4;
      break bb0;
    }
    let courses;
    if ($[6] !== sections[0]) {
      courses = [];
      if ("items" in sections[0]) {
        sections[0].items.forEach(item => {
          if (item && typeof item === "object" && "course" in item && item.course) {
            courses.push({
              ...(item.course as BrowseCourse),
              frontendTrackingId: item.frontendTrackingId,
              trackingId: item.trackingId
            });
          }
        });
      }
      $[6] = sections[0];
      $[7] = courses;
    } else {
      courses = $[7];
    }
    t3 = courses;
  }
  const courseRecommendations = t3;
  if (status === "loading" || !courseRecommendations?.length) {
    return null;
  }
  const t4 = sections[0]?.title ?? undefined;
  const t5 = showExtraDiscountBadge && isInstructorBundle;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      openInNewTab: true,
      showPriceInStatusLine: true
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== courseRecommendations) {
    t7 = courseRecommendations.map(_temp);
    $[9] = courseRecommendations;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== gettext) {
    t8 = gettext("Add all to cart");
    $[11] = gettext;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      size: "large"
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t8) {
    t10 = <AddToCartButton buyables={t7} addToCartButtonText={t8} forceRedirectToCart={true} buttonStyleProps={t9} />;
    $[14] = t7;
    $[15] = t8;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== courseRecommendations || $[18] !== t10 || $[19] !== t4 || $[20] !== t5) {
    t11 = <FrequentlyBoughtTogether title={t4} courses={courseRecommendations} showExtraDiscountBadge={t5} options={t6} addToCartButton={t10} />;
    $[17] = courseRecommendations;
    $[18] = t10;
    $[19] = t4;
    $[20] = t5;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
const FrequentlyBoughtTogetherWithTracking = withPriceEventTrackingContext(InternalFrequentlyBoughtTogether, {
  context: 'clp-bundle'
});
export const FrequentlyBoughtTogetherWithBundleUnit = withBundleUnit(FrequentlyBoughtTogetherWithTracking);
function _temp(course) {
  return {
    id: Number(course.id),
    buyableObjectType: "course",
    frontendTrackingId: course.frontendTrackingId
  };
}